import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

const MSG = {
  pending: "Waiting for your test call…", checking: "Call received, checking…", verified: "Forwarding works.",
  mismatch: "The call was forwarded from a different number. Check your forwarding and try again.",
  direct: "That call didn't come through forwarding. Call your business number from a different phone.", expired: "The test window ended. Start a new test.",
};

export function ForwardingTest({ bid, onVerified }) {
  const [busy, setBusy] = useState(false);
  const { data, refetch } = useQuery({
    queryKey: ["shally-fwd", bid], queryFn: async () => (await api.get(`/receptionist/setup/${bid}/forwarding-test`)).data,
    refetchInterval: (q) => (["pending", "checking"].includes(q.state.data?.test?.status) ? 4000 : false),
  });
  const st = data?.test?.status;
  useEffect(() => { if (data?.verified) { onVerified(); } }, [data?.verified]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data?.available) return null;
  const start = async () => {
    setBusy(true);
    try { await api.post(`/receptionist/setup/${bid}/forwarding-test`); await refetch(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 rounded-xl border border-slate-200 p-4 dark:border-slate-800" data-testid="onb-forwarding">
      <p className="text-sm text-slate-700 dark:text-slate-200">Your Pawkt receptionist number is <b data-testid="onb-pawkt-number">+{data.pawkt_number}</b>. Forward <b>+{data.business_number}</b> to it:</p>
      <ul className="mt-2 space-y-1 text-sm text-slate-600 dark:text-slate-400" data-testid="onb-fwd-instructions">
        {data.instructions.map((i) => <li key={i.who}><span className="font-medium text-slate-800 dark:text-slate-100">{i.who}:</span> <code className="break-all">{i.start}</code> · to stop: <code>{i.stop}</code></li>)}
      </ul>
      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400" data-testid="onb-fwd-cost">Test call: you call your business number from another phone within {data.window_minutes} minutes. Shally's AI is not used; you hear a short confirmation (about {data.cost.test_max_seconds} seconds) and the call ends. {data.cost.note}</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {!data.verified && <Button variant="outline" className="h-11 rounded-xl" disabled={busy || ["pending", "checking"].includes(st)} onClick={start} data-testid="onb-fwd-start">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PhoneCall className="mr-2 h-4 w-4" />}Start forwarding test</Button>}
        {(data.verified || st) && <span className={`text-sm ${data.verified ? "font-semibold text-emerald-700 dark:text-emerald-400" : "text-slate-600 dark:text-slate-300"}`} role="status" data-testid="onb-fwd-status">{data.verified ? MSG.verified : MSG[st]}</span>}
      </div>
    </div>);
}
